import React, { memo, useMemo, useState } from "react";

import { faCalendarDays } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleXmark, faGear, faPencil, faUser } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Checkbox, Icon, Link, Text } from "@performyard/py-components";

import { formatDateTime } from "../../../../components/FormattedDateTime";
import { LinkifiedText } from "../../../../components/LinkifiedText";
import ChecklistItemForm from "./ChecklistItemForm";
import FloatingDropdown from "./FloatingDropdown";
import { getMeetingGoalActionOptions } from "./MeetingGoalListItem";

import type { ChecklistItemProps } from "../types";

import { appendCurrentLocationAsFrom } from "../../../../utils/breadcrumbOrigin";

import { config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Individual checklist item for agenda or action items.
 * In edit mode, delegates entirely to <ChecklistItemForm /> so agenda + action
 * creation/editing share one inline form (only the action variant exposes due
 * date and assignee pickers).
 */
const ChecklistItem = memo(
	({
		id,
		name,
		dueDate,
		status,
		creatorId,
		userId,
		isEditing,
		assigneeId,
		assigneeName,
		isNewItem = false,
		itemType,
		onEdit,
		onStatusChange,
		onSave,
		goal,
		goalId,
		onGoalActionSelect,
		creatorName,
		workspaceParticipants,
	}: ChecklistItemProps): React.ReactElement => {
		const { isInteractionDisabled, meeting } = useMeetingContext();
		const [isGoalMenuOpen, setIsGoalMenuOpen] = useState(false);

		const isActionItem = itemType === "action";
		const testIdPrefix = isActionItem ? "action-item" : "agenda-item";
		const isGoalItem = Boolean(goalId || goal);

		const isChecked = status === "complete";
		const isCreator = userId === creatorId;
		const isAssignee = Boolean(assigneeId) && userId === assigneeId;
		const isCheckboxDisabled =
			isInteractionDisabled || (!isActionItem && !isCreator) || (isActionItem && !isAssignee && !isCreator);
		const goalActionOptions = useMemo(
			() => (goal ? getMeetingGoalActionOptions(goal, goal.permissions) : []),
			[goal]
		);

		const handleCheckboxChange = (checked: boolean): void => {
			if (isInteractionDisabled) return;
			onStatusChange(checked ? "complete" : "incomplete");
		};

		const renderMetadata = (): React.ReactElement | null => {
			const metadataItems: React.ReactElement[] = [];

			if (creatorName) {
				metadataItems.push(
					<span key="creator" className="flex items-center gap-0.5">
						<Icon icon={faUser} size={12} color="neutral-7" />
						<Text size="caption" weight="regular" color="neutral-7">
							Created by: {creatorName}
						</Text>
					</span>
				);
			}

			if (isActionItem && dueDate) {
				metadataItems.push(
					<span key="due-date" className="flex items-center gap-0.5">
						<Icon icon={faCalendarDays} size={12} color="neutral-7" />
						<Text size="caption" weight="regular" color="neutral-7">
							Due: {formatDateTime(dueDate, { hasTime: false, dateSize: "medium" })} •{" "}
							{config.assignment.dueTimeLabel}
						</Text>
					</span>
				);
			}

			if (isActionItem && assigneeName) {
				metadataItems.push(
					<span key="assignee" className="flex items-center gap-0.5">
						<Icon icon={faUser} size={12} color="neutral-7" />
						<Text size="caption" weight="regular" color="neutral-7">
							Assignee: {assigneeName}
						</Text>
					</span>
				);
			}

			if (metadataItems.length === 0) return null;

			return <div className="flex flex-wrap items-center gap-2">{metadataItems}</div>;
		};

		if (isEditing) {
			return (
				<ChecklistItemForm
					id={id}
					itemType={itemType}
					initialName={isNewItem ? "" : name}
					initialDueDate={dueDate}
					initialAssigneeId={assigneeId}
					isNewItem={isNewItem}
					workspaceParticipants={workspaceParticipants}
					onSave={onSave}
				/>
			);
		}

		return (
			<div className="group flex min-h-[32px] items-center gap-2 px-4 py-2">
				<div className="flex flex-1 items-center justify-between">
					<div className="flex items-center gap-2">
						<Checkbox
							checked={isChecked}
							onCheckedChange={handleCheckboxChange}
							disabled={isCheckboxDisabled}
							id={`${testIdPrefix}-${id}-checkbox`}
							data-testid={`${testIdPrefix}-${id}-checkbox`}
							dangerouslySetClassName="size-5"
						/>
						<div className="flex w-full flex-col gap-0.5">
							{isGoalItem ? (
								<div className="flex flex-wrap items-center gap-1">
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Goal:
									</Text>
									<Link
										href={appendCurrentLocationAsFrom(
											`/goal/${goal?._id || goalId}/`,
											meeting?.name
										)}
										forceHardReload={true}
										dangerouslySetClassName="no-underline hover:underline"
									>
										<Text size="paragraph-3" weight="regular" color="primary-5">
											{goal?.name || name}
										</Text>
									</Link>
								</div>
							) : (
								<Text size="paragraph-3" weight="regular" color="neutral-9">
									<LinkifiedText text={name} />
								</Text>
							)}
							{renderMetadata()}
						</div>
					</div>
					<div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
						{!isInteractionDisabled && (
							<>
								{isGoalItem ? (
									goal && onGoalActionSelect && goalActionOptions.length > 0 ? (
										<FloatingDropdown
											isOpen={isGoalMenuOpen}
											onOpenChange={setIsGoalMenuOpen}
											onSelect={(option) => onGoalActionSelect(option.id, goal)}
											trigger={
												<ButtonIcon
													icon={faGear}
													variant="standard"
													color="neutral"
													shade={5}
													size={16}
													data-testid={`${testIdPrefix}-${id}-goal-actions-button`}
													aria-label="Goal actions menu"
												/>
											}
											options={goalActionOptions.map((option) => ({
												id: option.id,
												label: (
													<Text size="caption" weight="regular" color="neutral-5">
														{option.label}
													</Text>
												),
											}))}
										/>
									) : goalId && !goal ? (
										<ButtonIcon
											icon={faGear}
											variant="standard"
											color="neutral"
											shade={5}
											size={16}
											disabled
											aria-label="Loading goal actions"
										/>
									) : null
								) : isCreator ? (
									<ButtonIcon
										icon={faPencil}
										variant="standard"
										color="neutral"
										shade={5}
										size={16}
										onClick={() => onEdit(id)}
										data-testid={`${testIdPrefix}-${id}-edit-button`}
										aria-label="Edit"
									/>
								) : null}
								{isCreator && (
									<ButtonIcon
										icon={faCircleXmark}
										variant="standard"
										color="neutral"
										shade={5}
										size={16}
										onClick={() => onStatusChange("deleted")}
										data-testid={`${testIdPrefix}-${id}-delete-button`}
										aria-label="Delete"
									/>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		);
	}
);

ChecklistItem.displayName = "ChecklistItem";

export default ChecklistItem;
